import React from "react";
import Link from "next/link";
import SubmenuLeftBottom from "./SubmenuLeftBottom";
import SubmenuChevron from "./SubmenuChevron";

interface SubmenuCard {
  id: number;
  title?: string | null;
  description?: string | null;
  url?: string | null;
}

interface SubmenuLink {
  id: number;
  href: string;
  label: string;
  // Supporting copy shown under the label. The CMS calls the field `hover`,
  // but in this layout it is rendered inline rather than on hover.
  hover?: string | null;
  target?: string | null;
  isExternal?: boolean;
  cards?: SubmenuCard[];
}

interface SubmenuLinkGroup {
  id: number;
  heading?: string | null;
  description?: string | null;
  links?: SubmenuLink[];
}

interface ResponsiveImages {
  desktop?: string;
  ipad?: string;
  mobile?: string;
}

interface SubmenuLinksLeftProps {
  right?: SubmenuLinkGroup[];
  bottomImages?: ResponsiveImages;
}

const borderClass =
  "border-[var(--color-blue-100)] hover:border-[var(--color-blue-500)] hover:text-[var(--color-blue-500)]";

// Compact style: a bare link name with a thin accent rule (Industries,
// Software Solutions), where a section lists many same-level pages.
const compactLinkClass = `pl-[12px] block border-l-2 leading-[150%] text-subtitle-4 font-[400] text-[var(--color-primary-text)] transition-all duration-300 ease-in-out ${borderClass}`;

// Descriptive style: a heading with a supporting paragraph underneath
// (About Us), used when the CMS supplies `hover` copy for the links.
const descriptiveLinkClass =
  "block leading-[130%] text-subtitle-1 font-bold text-[var(--color-primary-text)] transition-all duration-300 ease-in-out group-hover:text-[var(--color-blue-500)] !text-[16px]";

// Shared "flat link list" layout for menu sections where every entry points
// to a same-level page (Industries, Software Solutions, About Us) - there is
// no single "main" page per entry, so no bold page-name header is shown.
const SubmenuLinksLeft: React.FC<SubmenuLinksLeftProps> = ({
  right = [],
  bottomImages,
}) => {
  return (
    <div className="flex-1 w-full flex flex-col gap-2 justify-between">
      <div className="flex flex-wrap xl:gap-[24px] gap-[24px_15px] w-full">
        {right.map((group) => {
          // Links carrying `hover` copy get the heading-plus-paragraph
          // treatment from the design; sections whose links are bare page
          // names keep the compact list.
          const isDescriptive = group.links?.some((link) => link.hover);

          return (
            <div key={group.id} className="w-full flex flex-col gap-[16px]">
              {group.heading && (
                <h4 className="text-subtitle-3 font-bold text-[var(--color-primary-text)]">
                  {group.heading}
                </h4>
              )}
              {/* Skip values that are just a path/URL (e.g. the CMS has
                  "/solutions/crm-software" in a description field). */}
              {group.description &&
                !/^(\/|https?:\/\/)\S*$/.test(group.description.trim()) && (
                <p className="text-subtitle-4 font-semibold text-[var(--color-secondary-text)] opacity-[70%]">
                  {group.description}
                </p>
              )}
              <ul
                className={`flex flex-wrap ${
                  isDescriptive
                    ? "gap-x-[40px] gap-y-[16px] lg:gap-y-[40px]"
                    : "gap-x-[32px] gap-y-[16px]"
                }`}
              >
                {group.links?.map((link) => (
                  <li
                    key={link.id}
                    className={
                      isDescriptive
                        ? "w-full lg:w-[30%] flex flex-col gap-[12px] pb-[16px] border-b border-[var(--color-blue-100)] lg:pb-0 lg:border-0"
                        : "w-full sm:w-[45%] lg:w-[30%] flex flex-col gap-[12px]"
                    }
                  >
                    {isDescriptive ? (
                      // These rows have no children to expand, so the whole
                      // row navigates and the mobile chevron just signals that.
                      <Link
                        href={link.href || "#!"}
                        target={
                          link.isExternal ? link.target || "_blank" : undefined
                        }
                        rel={
                          link.isExternal ? "noopener noreferrer" : undefined
                        }
                        className="group flex items-center justify-between gap-[3px] lg:block"
                      >
                        <span className="flex flex-col gap-[8px] lg:gap-[12px]">
                          <span className={descriptiveLinkClass}>
                            {link.label}
                          </span>
                          {link.hover && (
                            <span className="block text-subtitle-4 font-[400] leading-[150%] text-[var(--color-secondary-text)] opacity-[70%] !tracking-[0px]">
                              {link.hover}
                            </span>
                          )}
                        </span>
                        <SubmenuChevron className="lg:hidden text-[var(--color-primary-text)]" />
                      </Link>
                    ) : (
                      <Link
                        href={link.href || "#!"}
                        target={
                          link.isExternal ? link.target || "_blank" : undefined
                        }
                        rel={
                          link.isExternal ? "noopener noreferrer" : undefined
                        }
                        className={compactLinkClass}
                      >
                        {link.label}
                      </Link>
                    )}
                    {link.cards && link.cards.length > 0 && (
                      <div className="flex flex-col gap-[8px] pl-[12px]">
                        {link.cards.map((card) => (
                          <Link
                            key={card.id}
                            href={card.url || "#!"}
                            className={`flex flex-col gap-[4px] p-[12px] border transition-all duration-300 ease-in-out ${borderClass}`}
                          >
                            {card.title && (
                              <h5 className="text-subtitle-4 font-bold text-[var(--color-primary-text)]">
                                {card.title}
                              </h5>
                            )}
                            {card.description && (
                              <p className="text-[12px] font-[400] text-[var(--color-secondary-text)] opacity-[70%]">
                                {card.description}
                              </p>
                            )}
                          </Link>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <SubmenuLeftBottom images={bottomImages} />
    </div>
  );
};

export default SubmenuLinksLeft;
